import type { IContentComponent } from "../../../models/Interfaces";
import { componentClassMap, layersForProperty } from "../../components/common/editorSiteFrame";
import { inlineHtmlRegions, normalizeInlineContainers } from "./inlineHtml";

// Patch a copy of the renderer document. Never change the saved component or
// fetch/save on every keystroke. Field wrappers reuse the rich editor mappings.
export const componentDraftPreview = (html: string, saved?: IContentComponent, draft?: IContentComponent): string => {
  if (!html || !saved || !draft) return html;
  const document = new DOMParser().parseFromString(html, "text/html");
  normalizeInlineContainers(document, inlineHtmlRegions(saved));
  const before = componentClassMap(saved);
  const after = componentClassMap(draft);
  for (const property of draft.BaseComponent.Properties) {
    const oldLayers = layersForProperty(saved.BaseComponent.Name, property.PropertyName, property.Type.Name, before);
    const newLayers = layersForProperty(draft.BaseComponent.Name, property.PropertyName, property.Type.Name, after);
    newLayers.forEach((layer, index) => {
      const anchor = layer.className?.split(/\s+/).find(name => name.startsWith("cms-"));
      if (!anchor) return;
      document.querySelectorAll<HTMLElement>(`.${anchor}`).forEach(target => {
        for (const name of oldLayers[index]?.className?.split(/\s+/) ?? []) if (name) target.classList.remove(name);
        for (const name of layer.className?.split(/\s+/) ?? []) if (name) target.classList.add(name);
        for (const key of new Set([...Object.keys(oldLayers[index]?.style ?? {}), ...Object.keys(layer.style ?? {})])) {
          (target.style as unknown as Record<string, string | number>)[key] = (layer.style as Record<string, string | number> | undefined)?.[key] ?? "";
        }
      });
    });
    // Class-only fields (image/title/button wrappers) also use the renderer's
    // stable cms-* names; retain its framework classes while replacing user CSS.
    if (/Class$/i.test(property.PropertyName)) {
      const marker = 'sbcms-draft-class-marker';
      const mapped = draft.BaseComponent.Properties.some(field => layersForProperty(draft.BaseComponent.Name,
        field.PropertyName, field.Type.Name, { ...after, [property.PropertyName]: marker })
        .some(layer => layer.className?.split(/\s+/).includes(marker)));
      if (mapped) continue;
      const suffix = property.PropertyName.replace(/Class$/i, "").replace(/[^a-z]/gi, "").toLowerCase();
      const previous = before[property.PropertyName]?.split(/\s+/) ?? [];
      const next = after[property.PropertyName]?.split(/\s+/) ?? [];
      document.querySelectorAll<HTMLElement>('[class]').forEach(target => {
        if (!Array.from(target.classList).some(name => name.startsWith('cms-') && name.replace(/[^a-z]/gi, '').toLowerCase().endsWith(suffix))) return;
        const rendererClasses = Array.from(target.classList).filter(name => name.startsWith('cms-'));
        previous.filter(name => name && !rendererClasses.includes(name) && !rendererClasses.includes(`cms-${name}`)).forEach(name => target.classList.remove(name));
        next.filter(Boolean).forEach(name => target.classList.add(name));
      });
    }
  }
  for (const region of inlineHtmlRegions(draft)) {
    const target = region.selector ? document.querySelectorAll(region.selector)[region.index] : document.querySelector('[data-contentcomponent-tag]');
    if (!target) continue;
    if (region.plainText) target.textContent = region.html;
    else target.innerHTML = region.html;
  }
  return `<!DOCTYPE html>${document.documentElement.outerHTML}`;
};
